import { AppThunk } from '../../../types';
import {
  setAdminDocumentHistoryDataAction,
  setAdminDocumentsDataAction,
  setAdminDocumentsErrorAction,
  setAdminDocumentsFetchStatusAction,
  setDocumentHistoryErrorAction,
  setDocumentHistoryFetchStatusAction,
  setDocumentsActionErrorAction,
  setDocumentsActionFetchStatusAction,
  setUploadDocumentErrorAction,
  setUploadDocumentFetchStatusAction,
  setEditDocumentErrorAction,
  setEditDocumentFetchStatusAction,
  setMergeDocumentsErrorAction,
  setMergeDocumentsFetchStatusAction,
} from './reducer';
import {
  createFetchedSuccess,
  createFetchedWithError,
  createFetchInProgress,
  createDefaultFetch,
} from 'types/rest/IFetchStatus';
import {
  deleteDocumentAsync,
  getDocumentHistoryAsync,
  getDocumentsAsync,
  mergeDocumentsAsync,
  publishDocumentAsync,
  unpublishDocumentAsync,
  publishDocumentHistoryAsync,
  unpublishDocumentHistoryAsync,
  updateDocumentAsync,
  updateDocumentMetadataAsync,
  uploadDocumentAsync,
  v4UploadDocsAsync,
} from '../../../../API/order/documents/adminDocumentsAPI';
import { eDocumentVisibilityType } from '../../../../types/order/documents/eDocumentVisibilityType';
import { IGetUser } from '../../../../types/users/IUser';
import { getUsers } from '../../../usersCollection/allUsers/dispatch';
import {
  IStandardDocumentData,
  IStandardDocumentVersionData,
  TStandardDocumentPublisherType,
} from '../../../../types/order/documents/shared/interfaces';
import { IUploadDocument } from '../../../../types/order/documents/upload/interfaces';
import { IDocumentCategory } from 'types/order/documents/IDocumentCategoryType';
import { eAPITags } from '../../../../API/eAPITags';
import { DocumentApi } from '../../../../API/order/documents/DocumentApi';

const getUsersForStandardDocuments =
  (documents: IStandardDocumentData[]): AppThunk =>
  async (dispatch) => {
    let users: IGetUser[] = [];
    documents.forEach((doc) => {
      const uploader = getUserByIdAndType(doc.uploaderType, doc.uploaderId);
      const lenderPublisher =
        doc.clientPublisherType && doc.clientPublisherId
          ? getUserByIdAndType(doc.clientPublisherType, doc.clientPublisherId)
          : null;
      const vendorPublisher =
        doc.vendorPublisherType && doc.vendorPublisherId
          ? getUserByIdAndType(doc.vendorPublisherType, doc.vendorPublisherId)
          : null;

      if (uploader) {
        users.push(uploader);
      }
      if (lenderPublisher) {
        users.push(lenderPublisher);
      }
      if (vendorPublisher) {
        users.push(vendorPublisher);
      }
    });
    users = users.filter((user, index, self) => index === self.findIndex((t) => t.userId === user.userId));
    await dispatch(getUsers(users));
  };

export const getAdminDocuments =
  (orderId: string, categories?: IDocumentCategory[]): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setAdminDocumentsErrorAction(null));
      dispatch(setAdminDocumentsFetchStatusAction(createFetchInProgress()));
      const response: IStandardDocumentVersionData[] = await getDocumentsAsync(orderId, categories);
      dispatch(setAdminDocumentsDataAction({ orderId, value: response }));
      dispatch(setAdminDocumentsFetchStatusAction(createFetchedSuccess()));
      dispatch(getUsersForStandardDocuments(response));
    } catch (e: any) {
      dispatch(setAdminDocumentsFetchStatusAction(createFetchedWithError()));
      dispatch(setAdminDocumentsErrorAction(e.message));
    }
  };

export const deleteDocument =
  (orderId: string, documentId: string, versionId: string): AppThunk =>
  async (dispatch, getState) => {
    const documents = getState().order.documents.adminDocuments.documents;
    try {
      dispatch(setDocumentsActionErrorAction(null));
      dispatch(setDocumentsActionFetchStatusAction(createFetchInProgress()));
      await deleteDocumentAsync(documentId, versionId);
      dispatch(
        setAdminDocumentsDataAction({
          orderId,
          value: documents[orderId].filter((doc) => doc.id !== documentId),
        })
      );
      dispatch(setDocumentsActionFetchStatusAction(createFetchedSuccess()));
      dispatch(DocumentApi.util.invalidateTags([{ type: eAPITags.SectionData, id: orderId }]));
    } catch (e: any) {
      dispatch(setDocumentsActionFetchStatusAction(createFetchedWithError()));
      dispatch(setDocumentsActionErrorAction(e.message));
    }
  };

export const uploadDocuments =
  (
    orderId: string,
    documents: IUploadDocument[],
    existingDocuments: IStandardDocumentVersionData[],
    useOldApi?: boolean,
    callback?: (values: string[]) => void | Promise<void>
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setUploadDocumentErrorAction(null));
      dispatch(setUploadDocumentFetchStatusAction(createFetchInProgress()));
      // Use Old API allows for compatibility with older versions of the API, mainly to fix utilizing this API call on the client dashboard
      const upload = Promise.all(
        documents.map(async (document: IUploadDocument) =>
          useOldApi
            ? await v4UploadDocsAsync(orderId, document)
            : await uploadDocumentAsync(orderId, document, docExists(document, existingDocuments))
        )
      );
      const fileResults = await upload;
      callback && (await callback(fileResults));
      dispatch(setUploadDocumentFetchStatusAction(createFetchedSuccess()));
      dispatch(DocumentApi.util.invalidateTags([{ type: eAPITags.SectionData, id: orderId }]));
      return Promise.resolve();
    } catch (e: any) {
      dispatch(setUploadDocumentFetchStatusAction(createFetchedWithError()));
      dispatch(setUploadDocumentErrorAction(e.message));
      return Promise.reject();
    }
  };

export const resetUploadDocumentStatus =
  (orderId: string): AppThunk =>
  async (dispatch) => {
    dispatch(setUploadDocumentFetchStatusAction(createDefaultFetch()));
    dispatch(setUploadDocumentErrorAction(null));
    // update list of document. A list can be updated or new items can be added depends on uploaded documents
    await dispatch(getAdminDocuments(orderId));
  };

export const publishDocument =
  (orderId: string, documentId: string, visibilityType: eDocumentVisibilityType): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setDocumentsActionErrorAction(null));
      dispatch(setDocumentsActionFetchStatusAction(createFetchInProgress()));
      await publishDocumentAsync(documentId, visibilityType);
      dispatch(setDocumentsActionFetchStatusAction(createFetchedSuccess()));
      dispatch(getAdminDocuments(orderId));
    } catch (e: any) {
      dispatch(setDocumentsActionFetchStatusAction(createFetchedWithError()));
      dispatch(setDocumentsActionErrorAction(e.message));
    }
  };

export const unpublishDocument =
  (orderId: string, documentId: string, visibilityType: eDocumentVisibilityType): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setDocumentsActionErrorAction(null));
      dispatch(setDocumentsActionFetchStatusAction(createFetchInProgress()));
      await unpublishDocumentAsync(documentId, visibilityType);
      dispatch(setDocumentsActionFetchStatusAction(createFetchedSuccess()));
      dispatch(getAdminDocuments(orderId));
    } catch (e: any) {
      dispatch(setDocumentsActionFetchStatusAction(createFetchedWithError()));
      dispatch(setDocumentsActionErrorAction(e.message));
    }
  };

export const onSigningDocument =
  (orderId: string, documentId: string, versionId: string, signing: boolean): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setDocumentsActionErrorAction(null));
      dispatch(setDocumentsActionFetchStatusAction(createFetchInProgress()));
      await updateDocumentAsync(documentId, versionId, { requiredAtSigning: signing });
      dispatch(getAdminDocuments(orderId));
    } catch (e: any) {
      dispatch(setDocumentsActionFetchStatusAction(createFetchedWithError()));
      dispatch(setDocumentsActionErrorAction(e.message));
    }
  };

export const onFundingDocument =
  (orderId: string, documentId: string, versionId: string, funding: boolean): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setDocumentsActionErrorAction(null));
      dispatch(setDocumentsActionFetchStatusAction(createFetchInProgress()));
      await updateDocumentAsync(documentId, versionId, { funding: funding });
      dispatch(getAdminDocuments(orderId));
    } catch (e: any) {
      dispatch(setDocumentsActionFetchStatusAction(createFetchedWithError()));
      dispatch(setDocumentsActionErrorAction(e.message));
    }
  };

export const publishDocumentForHistory =
  (documentId: string, versionId: string, visibilityType: eDocumentVisibilityType): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setDocumentHistoryErrorAction(null));
      dispatch(setDocumentHistoryFetchStatusAction(createFetchInProgress()));
      await publishDocumentHistoryAsync(documentId, versionId, visibilityType);
      dispatch(await getAdminDocumentHistory(documentId));
    } catch (e: any) {
      dispatch(setDocumentHistoryFetchStatusAction(createFetchedWithError()));
      dispatch(setDocumentHistoryErrorAction(e.message));
    }
  };

export const unpublishDocumentForHistory =
  (documentId: string, versionId: string, visibilityType: eDocumentVisibilityType): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setDocumentHistoryErrorAction(null));
      dispatch(setDocumentHistoryFetchStatusAction(createFetchInProgress()));
      await unpublishDocumentHistoryAsync(documentId, versionId, visibilityType);
      dispatch(await getAdminDocumentHistory(documentId));
    } catch (e: any) {
      dispatch(setDocumentHistoryFetchStatusAction(createFetchedWithError()));
      dispatch(setDocumentHistoryErrorAction(e.message));
    }
  };

export const initAdminDocumentHistory =
  (documentId: string): AppThunk =>
  async (dispatch, getState) => {
    const fetchStatus = getState().order.documents.adminDocuments.documentsHistoryFetchStatus;
    if (!fetchStatus.fetching && !fetchStatus.fetched) {
      dispatch(getAdminDocumentHistory(documentId));
    }
  };

export const getAdminDocumentHistory =
  (documentId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setDocumentHistoryErrorAction(null));
      dispatch(setDocumentHistoryFetchStatusAction(createFetchInProgress()));
      const response: IStandardDocumentVersionData[] = await getDocumentHistoryAsync(documentId);
      await dispatch(getUsersForStandardDocuments(response));
      dispatch(setAdminDocumentHistoryDataAction({ orderId: documentId, value: response }));
      dispatch(setDocumentHistoryFetchStatusAction(createFetchedSuccess()));
    } catch (e: any) {
      dispatch(setDocumentHistoryFetchStatusAction(createFetchedWithError()));
      dispatch(setDocumentHistoryErrorAction(e.message));
    }
  };

export const updateDocumentMetadata =
  (orderId: string, document: IStandardDocumentData): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setEditDocumentErrorAction(null));
      dispatch(setEditDocumentFetchStatusAction(createFetchInProgress()));
      await updateDocumentMetadataAsync(document.id, document.metadata!);
      dispatch(setEditDocumentFetchStatusAction(createFetchedSuccess()));
      return Promise.resolve();
    } catch (e: any) {
      dispatch(setEditDocumentFetchStatusAction(createFetchedWithError()));
      dispatch(setEditDocumentErrorAction(e.message));
      return Promise.reject();
    }
  };

export const resetUpdateDocument =
  (orderId: string): AppThunk =>
  async (dispatch) => {
    dispatch(setEditDocumentFetchStatusAction(createDefaultFetch()));
    dispatch(setEditDocumentErrorAction(null));
    dispatch(getAdminDocuments(orderId));
  };

export const resetDeleteDocument = (): AppThunk => async (dispatch) => {
  dispatch(setDocumentsActionErrorAction(null));
  dispatch(setDocumentsActionFetchStatusAction(createDefaultFetch()));
};

export const mergeDocuments =
  (orderId: string, documentIds: string[], filename: string | undefined, category: IDocumentCategory): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setMergeDocumentsErrorAction(null));
      dispatch(setMergeDocumentsFetchStatusAction(createFetchInProgress()));
      await mergeDocumentsAsync(orderId, documentIds, filename, category);
      dispatch(setMergeDocumentsFetchStatusAction(createFetchedSuccess()));
      return Promise.resolve();
    } catch (e: any) {
      dispatch(setMergeDocumentsFetchStatusAction(createFetchedWithError()));
      dispatch(setMergeDocumentsErrorAction(e.message));
      return Promise.reject();
    }
  };

export const resetMergeDocuments =
  (orderId: string): AppThunk =>
  async (dispatch) => {
    dispatch(setMergeDocumentsErrorAction(null));
    dispatch(setMergeDocumentsFetchStatusAction(createDefaultFetch()));
    dispatch(getAdminDocuments(orderId));
  };

const getUserByIdAndType = (userType: TStandardDocumentPublisherType, id: string): IGetUser | null => {
  switch (userType) {
    case 'Admin':
      return { userType: 'ADMIN', userId: id };
    case 'LenderUser':
      return { userType: 'LENDER_USER', userId: id };
    case 'VendorUser':
      return { userType: 'VENDOR_USER', userId: id };
    case 'ClientUser':
      return { userType: 'CLIENT_USER', userId: id };
    case 'ADMIN':
      return { userType: 'ADMIN', userId: id };
    case 'LENDER_USER':
      return { userType: 'LENDER_USER', userId: id };
    case 'VENDOR_USER':
      return { userType: 'VENDOR_USER', userId: id };
    case 'CLIENT_USER':
      return { userType: 'CLIENT_USER', userId: id };
    default:
      return null;
  }
};

const docExists = (
  category: IUploadDocument,
  documents: IStandardDocumentVersionData[]
): IStandardDocumentVersionData | undefined => {
  return documents.find((document) => document.category === category.category);
};
